import React, { useEffect, useState } from 'react';
import { Button, Grid2 as Grid } from "@mui/material";
import { initKeycloak, getKeycloakService } from '../services/keycloak';
import { useTranslation } from 'react-i18next';
import LockIcon from '@mui/icons-material/Lock';
import Typography from "@mui/material/Typography";

export function KeycloakAuth({ onAuthSuccess, onAuthError }) {
    const { t } = useTranslation();
    const [keycloakReady, setKeycloakReady] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const keycloakConfig = {
            realmUrl: process.env.REACT_APP_KEYCLOAK_REALM_URL,
            clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID
        };

        if (!keycloakConfig.realmUrl || !keycloakConfig.clientId) {
            console.log('Keycloak not configured');
            return;
        }

        const init = async () => {
            try {
                const keycloakService = initKeycloak(keycloakConfig);
                if (keycloakService) {
                    await keycloakService.init();
                    
                    keycloakService.onTokenExpired(() => {
                        keycloakService.updateToken(30).then((refreshed) => {
                            if (refreshed) {
                                console.log('Token refreshed');
                            }
                        });
                    });

                    keycloakService.onAuthSuccess(() => {
                        setIsAuthenticated(keycloakService.isAuthenticated());
                        if (onAuthSuccess) {
                            onAuthSuccess(keycloakService.getToken());
                        }
                    });

                    keycloakService.onAuthError((error) => {
                        console.error('Keycloak auth error:', error);
                        if (onAuthError) {
                            onAuthError(error);
                        }
                    });

                    setKeycloakReady(true);
                    setIsAuthenticated(keycloakService.isAuthenticated());

                    // Auto-update token periodically
                    setInterval(() => {
                        keycloakService.updateToken(30);
                    }, 30000);
                }
            } catch (error) {
                console.error('Failed to initialize Keycloak:', error);
                if (onAuthError) {
                    onAuthError(error);
                }
            }
        };

        init();
    }, [onAuthSuccess, onAuthError]);

    const handleLogin = async () => {
        const keycloakService = getKeycloakService();
        if (keycloakService) {
            await keycloakService.login();
        }
    };

    const handleLogout = async () => {
        const keycloakService = getKeycloakService();
        if (keycloakService) {
            await keycloakService.logout();
            setIsAuthenticated(false);
        }
    };

    if (!keycloakReady) {
        return null; // Don't show anything if Keycloak is not configured or not ready
    }

    return (
        <Grid container spacing={2} direction="column" alignItems="center">
            {!isAuthenticated ? (
                <Grid size={{xs: 12, sm: 8, md: 4, lg: 3, xl: 2}}>
                    <Button 
                        size="large" 
                        fullWidth 
                        sx={{height: 65, borderRadius: 7, textTransform: 'none'}} 
                        variant="contained"
                        startIcon={<LockIcon/>} 
                        onClick={handleLogin}
                    >
                        <Typography fontSize="1.2rem">
                            {t('components:authorize_buttons_container.keycloak_login', 'Login with Keycloak')}
                        </Typography>
                    </Button>
                </Grid>
            ) : (
                <Grid size={{xs: 12, sm: 8, md: 4, lg: 3, xl: 2}}>
                    <Button 
                        size="large" 
                        fullWidth 
                        sx={{height: 65, borderRadius: 7, textTransform: 'none'}} 
                        variant="outlined"
                        onClick={handleLogout}
                    >
                        <Typography fontSize="1.2rem">
                            {t('components:authorize_buttons_container.keycloak_logout', 'Logout')}
                        </Typography>
                    </Button>
                </Grid>
            )}
        </Grid>
    );
}

